/**
 * Animación de apertura/cierre para los acordeones de FAQ del tema.
 *
 * 20 agosto 2026, TERCERA versión. Historial del bug, porque el motivo real no es evidente:
 *
 *  1ª versión: medir el alto con getBoundingClientRect y animar `height` con la Web
 *     Animations API. Se veía un "teletransporte" al llegar a la primera línea de texto.
 *  2ª versión: técnica CSS `grid-template-rows: 0fr → 1fr` (no mide píxeles, el grid calcula
 *     el alto real en cada fotograma). Mejoró, pero Fran reportó que **la primera vez** que
 *     se pulsa una pregunta salta igual, y a partir del segundo clic ya va fluido.
 *
 * Causa real (la que explica el "solo la primera vez"): mientras un <details> está cerrado,
 * el navegador NO maqueta su contenido — está oculto a nivel de render, no solo visualmente.
 * En el primer clic, el navegador tiene que maquetar ese texto por primera vez (calcular
 * saltos de línea, alto, etc.) EN EL MISMO fotograma en el que empieza la transición, así que
 * no tiene un valor "de antes" desde el que interpolar y pega el salto. A partir del segundo
 * clic el contenido ya está maquetado y cacheado, y por eso va fluido.
 *
 * Arreglo: que el contenido esté SIEMPRE maquetado. Se deja el atributo `open` puesto de
 * forma permanente en todos los <details> (así el navegador nunca esconde el contenido a
 * nivel de render) y quien decide si se ve o no es una clase nuestra, `is-abierto`, con la
 * transición de grid. Como el contenido nunca deja de estar maquetado, la primera animación
 * es idéntica a la décima.
 *
 * Sin JS (o si algo falla), no se pone la clase `kotashi-acordeon-js` en <html>, así que
 * ninguna de las reglas de plegado se aplica y los <details> funcionan como siempre: se abren
 * y cierran de golpe, pero funcionan. Nunca se queda contenido inaccesible.
 */
(() => {
  if (window.__kotashiAcordeonInit) return;
  window.__kotashiAcordeonInit = true;

  const SELECTOR_ITEM = '.kotashi-faq__item, .kotashi-ficha__faq-item';

  // Solo a partir de aquí el CSS se atreve a plegar nada (ver nota de arriba sobre el
  // fallback sin JS).
  document.documentElement.classList.add('kotashi-acordeon-js');

  /**
   * Deja `open` puesto para siempre en cada acordeón, para que su contenido esté maquetado
   * desde el principio. Se llama también tras cambios del DOM porque Horizon re-renderiza
   * secciones enteras al editar en el personalizador.
   */
  const prepararTodos = () => {
    for (const item of document.querySelectorAll(SELECTOR_ITEM)) {
      if (!item.open) item.open = true;
      const resumen = item.querySelector(':scope > summary');
      if (resumen && !resumen.hasAttribute('aria-expanded')) {
        resumen.setAttribute('aria-expanded', item.classList.contains('is-abierto') ? 'true' : 'false');
      }
    }
  };

  prepararTodos();
  document.addEventListener('DOMContentLoaded', prepararTodos);
  // Por si Horizon inserta secciones nuevas después de cargar (editor de temas).
  new MutationObserver(prepararTodos).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('click', (evento) => {
    const resumen = evento.target.closest(
      '.kotashi-faq__item > summary, .kotashi-ficha__faq-item > summary'
    );
    if (!resumen) return;

    const item = resumen.parentElement;

    // `open` está puesto siempre, así que el toggle nativo del navegador lo CERRARÍA de
    // verdad (escondiendo el contenido a nivel de render y rompiendo justo lo que arregla
    // este script). Se bloquea y se gestiona con la clase.
    evento.preventDefault();

    const abierto = item.classList.toggle('is-abierto');
    resumen.setAttribute('aria-expanded', abierto ? 'true' : 'false');
    if (!item.open) item.open = true;
  });
})();
